import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { COLORS, FONTS } from '../../../constants';
import { useIsDesktop } from '../../../hooks/useIsDeskTop';
import { IBookingState } from '../types';
import { CampPriceData, ICamp } from '../../../types/camp.types';
import dayjs from 'dayjs';
import { convertPrice, convertToAmPm } from '../../../services/utils';
import { STYLE_VARS } from '../../../constants/style-vars';
import { getDatesBetween } from '../../../services/utils/dateConvert';
import { DISH_TYPES } from '../../../constants/dishs';
import { TabelDeleteableItem } from './Table/TabelDeleteableItem';
import { IExtraDish } from '../../../types/dishes.types';
import { IExtraActivity } from '../../../types/activities.types';

type ProductTableProps = {
  state: IBookingState;
  booking: ICamp;
  priceData?: CampPriceData;
  isShowRoom?: boolean;
  setState?: (state: Partial<IBookingState>) => void;
  editable?: boolean;
};
interface ITableItem {
  product: string;
  desc: string;
  price: string;
}

const ProductTable: FC<ProductTableProps> = ({
  state,
  booking,
  isShowRoom = false,
  editable = false,
  setState,
}) => {
  const betweenDates = getDatesBetween(
    state.start_date,
    state.end_date,
    booking.room_availability
  );
  const isDesktop = useIsDesktop();

  const showPrice = (price: string | number) => {
    if (price) {
      return convertPrice(Number(price));
    } else {
      return 'Free';
    }
  };

  const handleDeleteDish = useCallback(
    (deleteDish: IExtraDish) => {
      const updatedDishes = state.extra_dishes.filter(
        (item) =>
          !(
            item.dish.id === deleteDish.dish.id &&
            item.date === deleteDish.date &&
            item.type === deleteDish.type
          )
      );
      if (setState)
        setState({
          extra_dishes: updatedDishes,
        });
    },
    [setState, state.extra_dishes]
  );

  const handleDeleteActivities = useCallback(
    (deleteActivity: IExtraActivity) => {
      const updatedActivities = state.extra_activities.filter(
        (item) =>
          !(
            item.activity.id === deleteActivity.activity.id &&
            item.date === deleteActivity.date
          )
      );
      if (setState)
        setState({
          extra_activities: updatedActivities,
        });
    },
    [setState, state.extra_activities]
  );

  const renderTableItemRow = useCallback((item: ITableItem) => {
    return (
      <View style={{ flexDirection: 'row', marginBottom: 15 }}>
        <View style={{ width: '71%' }}>
          <Text style={styles.tableContentText}>{item.product}</Text>
          <Text style={[styles.tableContentText, { fontSize: 16 }]}>
            {item.desc}
          </Text>
        </View>
        <View style={{ width: '29%' }}>
          <Text style={styles.tableContentText}>{item.price}</Text>
        </View>
      </View>
    );
  }, []);

  const renderCamp = useMemo(() => {
    let campPrice = state.price_data?.camp_price
      ? state.price_data?.camp_price / 100
      : 0;

    return (
      <View style={{ marginBottom: 10 }}>
        <View style={{ marginBottom: 8 }}>
          <Text style={styles.tableHeaderText}>Camp Type</Text>
        </View>
        {renderTableItemRow({
          product: booking.name ?? '',
          desc: '',
          price: campPrice ? convertPrice(Number(campPrice)) : 'Included',
        })}
      </View>
    );
  }, [booking.name, renderTableItemRow, state.price_data?.camp_price]);

  const renderRooms = useMemo(() => {
    return (
      state.rooms.length > 0 && (
        <View style={{ marginBottom: 10 }}>
          <View style={{ marginBottom: 8 }}>
            <Text style={styles.tableHeaderText}>Rooms</Text>
          </View>
          <View>
            {state.rooms.map((room) => {
              const roomFind = state.price_data?.rooms.find(
                (item) => item.id === room.room_id
              );

              if (roomFind) {
                return (
                  <>
                    {renderTableItemRow({
                      product: roomFind.name.en,
                      desc: `No. Of Room: ${room.quantity}${
                        room.is_additional_bed ? '\nAddtional bed' : ''
                      }`,
                      price: `${showPrice(roomFind.total / 100)}`,
                    })}
                  </>
                );
              }
            })}
          </View>
        </View>
      )
    );
  }, [renderTableItemRow, state.price_data, state.rooms]);

  const renderMeals = useMemo(() => {
    return (
      <View style={{ marginBottom: 10 }}>
        <View style={{ marginBottom: 8 }}>
          <Text style={styles.tableHeaderText}>Meals</Text>
        </View>
        {booking.included_meal.breakfast &&
          renderTableItemRow({
            product: 'Breakfast',
            desc: booking.included_meal.breakfast.name,
            price: showPrice(booking.included_meal.breakfast.price),
          })}
        {booking.included_meal.lunch &&
          renderTableItemRow({
            product: 'Lunch',
            desc: booking.included_meal.lunch.name,
            price: showPrice(booking.included_meal.lunch.price),
          })}
        {state.dinner_meal &&
          renderTableItemRow({
            product: 'Dinner',
            desc: state.dinner_meal.name,
            price: showPrice(state.dinner_meal.price),
          })}
      </View>
    );
  }, [
    booking.included_meal.breakfast,
    booking.included_meal.lunch,
    renderTableItemRow,
    state.dinner_meal,
  ]);

  const renderExtraMeals = useMemo(() => {
    return state.extra_dishes.length > 0 ? (
      <View>
        <View style={{ marginBottom: 8 }}>
          <Text style={styles.tableHeaderText}>Extra Meals</Text>
        </View>
        {betweenDates.map((date, index) => {
          const dateDishes = state.extra_dishes.filter(
            (dish) => dish.date === date
          );
          const breakfastDateDishes = dateDishes.filter(
            (dish) => dish.type === DISH_TYPES.BREAKFAST
          );
          const lunchDateDishes = dateDishes.filter(
            (dish) => dish.type === DISH_TYPES.LUNCH
          );
          const dinnerDateDishes = dateDishes.filter(
            (dish) => dish.type === DISH_TYPES.DINNER
          );

          return dateDishes.length > 0 ? (
            <View style={{ marginBottom: 10 }}>
              <Text style={styles.tableSubHeaderText}>
                {`Day ${index + 1} - ${dayjs(new Date(date)).format(
                  'DD/MM/YYYY'
                )}`}
              </Text>
              {/* Breakfast */}
              {breakfastDateDishes.length > 0 && (
                <View>
                  <Text style={[styles.tableContentText, { marginBottom: 5 }]}>
                    Breakfast
                  </Text>
                  {breakfastDateDishes.map((item) => {
                    return (
                      <TabelDeleteableItem
                        name={item.dish.name}
                        description={`No. Of Meal: ${item.quantity}`}
                        price={convertPrice(item.dish.price * item.quantity)}
                        onDeleteItem={() => {
                          handleDeleteDish(item);
                        }}
                        isCanDelete={editable}
                      />
                    );
                  })}
                </View>
              )}
              {/* Lunch */}
              {lunchDateDishes.length > 0 && (
                <View>
                  <Text style={[styles.tableContentText, { marginBottom: 5 }]}>
                    Lunch
                  </Text>
                  {lunchDateDishes.map((item) => {
                    return (
                      <TabelDeleteableItem
                        name={item.dish.name}
                        description={`No. Of Meal: ${item.quantity}`}
                        price={convertPrice(item.dish.price * item.quantity)}
                        onDeleteItem={() => {
                          handleDeleteDish(item);
                        }}
                        isCanDelete={editable}
                      />
                    );
                  })}
                </View>
              )}
              {/* Dinner */}
              {dinnerDateDishes.length > 0 && (
                <View>
                  <Text style={[styles.tableContentText, { marginBottom: 5 }]}>
                    Dinner
                  </Text>
                  {dinnerDateDishes.map((item) => {
                    return (
                      <TabelDeleteableItem
                        name={item.dish.name}
                        description={`No. Of Meal: ${item.quantity}`}
                        price={convertPrice(item.dish.price * item.quantity)}
                        onDeleteItem={() => {
                          handleDeleteDish(item);
                        }}
                        isCanDelete={editable}
                      />
                    );
                  })}
                </View>
              )}
            </View>
          ) : (
            <></>
          );
        })}
      </View>
    ) : (
      <></>
    );
  }, [betweenDates, state.extra_dishes, handleDeleteDish, editable]);

  const renderExtraActivities = useMemo(() => {
    return state.extra_activities.length > 0 ? (
      <View>
        <View style={{ marginBottom: 8 }}>
          <Text style={styles.tableHeaderText}>Extra Activities</Text>
        </View>
        {betweenDates.map((date, index) => {
          const dateActivities = state.extra_activities.filter(
            (activity) => activity.date === date
          );

          return dateActivities.length > 0 ? (
            <View style={{ marginBottom: 10 }}>
              <Text style={styles.tableSubHeaderText}>
                {`Day ${index + 1} - ${dayjs(new Date(date)).format(
                  'DD/MM/YYYY'
                )}`}
              </Text>
              {dateActivities.length > 0 && (
                <View>
                  {dateActivities.map((item) => {
                    return (
                      <TabelDeleteableItem
                        name={item.activity.name}
                        description={`${convertToAmPm(
                          item.activity.start_time
                        )} - ${convertToAmPm(
                          item.activity.end_time
                        )}\nNo. Of People: ${item.attendees}`}
                        price={convertPrice(
                          item.activity.price * item.attendees
                        )}
                        onDeleteItem={() => {
                          handleDeleteActivities(item);
                        }}
                        isCanDelete={editable}
                      />
                    );
                  })}
                </View>
              )}
            </View>
          ) : (
            <></>
          );
        })}
      </View>
    ) : (
      <></>
    );
  }, [state, betweenDates, handleDeleteActivities, editable]);

  const renderActivities = useMemo(() => {
    return (
      booking.activities && (
        <View style={{ marginBottom: 10 }}>
          <View style={{ marginBottom: 8 }}>
            <Text style={styles.tableHeaderText}>Included Activities</Text>
          </View>
          {booking.activities.map((item) => {
            return renderTableItemRow({
              product: item.name,
              desc: `${dayjs(new Date(state.start_date)).format(
                'DD.MM.YYYY'
              )}\n${dayjs(new Date(item.start_time)).format(
                'h:mm a'
              )} - ${dayjs(new Date(item.end_time)).format('h:mm a')}\nVenue: ${
                item.venue
              }`,
              price: showPrice(item.price),
            });
          })}
        </View>
      )
    );
  }, [booking.activities, renderTableItemRow, state.start_date]);

  const totalPrice = useMemo(() => {
    const campPrice = state.price_data?.camp_price ?? 0;
    const roomPrice =
      state.price_data?.rooms.reduce(
        (total, room) => total + (room.total ?? 0) + (room.bed_price ?? 0),
        0
      ) ?? 0;

    const campRoomPrice = booking.room_availability ? roomPrice : campPrice;
    const mealPrice = state.extra_dishes.reduce(
      (total, dish) => total + dish.quantity * dish.dish.price_in_cents,
      0
    );
    const activityPrice = state.extra_activities.reduce(
      (total, activity) =>
        total + activity.attendees * activity.activity.price_per_cents,
      0
    );

    return campRoomPrice + mealPrice + activityPrice;
  }, [
    booking.room_availability,
    state.extra_dishes,
    state.price_data,
    state.extra_activities,
  ]);

  return (
    <View
      style={{
        backgroundColor: '#f9f2f1',
        padding: 14,
        borderRadius: 18,
        flexDirection: 'column',
        marginBottom: 25,
      }}
    >
      <View>
        <Text
          style={{
            fontFamily: FONTS.AvenirMedium,
            fontWeight: '800',
            fontSize: 19,
            color: COLORS.BLACK,
            textAlign: 'center',
            marginBottom: 15,
            paddingHorizontal: 10,
            textTransform: 'uppercase',
          }}
        >
          {booking.name}
        </Text>
      </View>
      <View
        style={{
          flexDirection: 'row',
          paddingBottom: 10,
          borderBottomColor: COLORS.LIGHT_LIGHT_GREY,
          borderBottomWidth: 1,
          marginBottom: 8,
        }}
      >
        <View style={{ width: '71%' }}>
          <Text style={styles.tableHeaderText}>Product</Text>
        </View>
        <View
          style={[
            {
              width: '29%',
            },
            isDesktop()
              ? {
                  flexDirection: 'row',
                  alignItems: 'center',
                }
              : {
                  flexDirection: 'column',
                },
          ]}
        >
          <Text style={styles.tableHeaderText}>Price</Text>
          <Text
            style={{
              fontFamily: FONTS.AvenirMedium,
              fontSize: 12,
              fontWeight: '500',
              marginLeft: 3,
              color: COLORS.GREY,
            }}
          >
            {'(HKD)'}
          </Text>
        </View>
      </View>
      <View
        style={{
          borderBottomColor: COLORS.LIGHT_LIGHT_GREY,
          borderBottomWidth: 1,
          marginBottom: 10,
        }}
      >
        {renderCamp}
        {renderRooms}
        {!isShowRoom && (
          <>
            {renderMeals}
            {renderExtraMeals}
            {renderActivities}
            {renderExtraActivities}
          </>
        )}
      </View>
      {!isShowRoom && (
        <View style={{ flexDirection: 'row', marginBottom: 20 }}>
          <View style={{ width: '30%' }}>
            <Text style={[styles.tableContentText, { color: COLORS.BLACK }]}>
              Total Price:
            </Text>
          </View>
          <View style={{ width: '41%' }}>
            <Text style={[styles.tableContentText, { color: COLORS.BLACK }]}>
              {state.price_data?.price_type === 'subsidized' &&
                'Subsidised booking rate applied'}
            </Text>
          </View>
          <View style={{ width: '29%' }}>
            <Text style={[styles.tableContentText, { color: COLORS.BLACK }]}>
              {state.price_data ? showPrice(totalPrice / 100) : ''}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

export default ProductTable;

const styles = StyleSheet.create({
  tableHeaderText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '800',
    fontSize: 19,
    color: COLORS.BLACK,
  },
  tableSubHeaderText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 17,
    marginBottom: 7,
    color: COLORS.DARKER_GREY,
  },
  tableContentText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 17,
    color: COLORS.GREY,
    paddingRight: 5,
  },
  countContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  countBtn: {},
  countBtnIcon: {
    width: 20,
    height: 20,
  },
  countNumber: {
    fontSize: 15,
    color: COLORS.GREY,
    fontFamily: STYLE_VARS.avenirMediumFont,
  },
});
